import { useCreateProjectWithDocumentAction } from "@/action/use-create-project-with-document-action";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenuButton,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useSettingData } from "@/data/use-settings-data";
import { useGroupTemplatesData } from "@/data/use-templates-data";
import { Template } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faHome,
  faPlus,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import {
  Link,
  useLocation,
  useMatchRoute,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useKitsData } from "../../data/use-kits-data";
import { useGroupSlug } from "../../hooks/use-group-slug";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useUserUuid } from "../../hooks/use-user-uuid";
import { createContentPortal } from "../../lib/create-content-portal";
import { cn } from "../../lib/utils";
import { FeedbackButton } from "../FeedbackButton/FeedbackButton";
import {
  DocumentIcon,
  StructuredDocumentIcon,
} from "../display/display-icon/artifacts";
import { getDefaultKitIcon } from "../display/display-name/defaults";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Separator } from "../ui/separator";
import { GroupAdminMenu } from "./menus/GroupAdminMenu";
import { SidebarGroupSwitcher } from "./menus/SidebarGroupSwitcher";
import { SidebarUser } from "./menus/SidebarUser";
import { UserSettingsMenu } from "./menus/UserSettingsMenu";

const { ContentContainer, ContentPortal, useOnContentCheck } =
  createContentPortal("AppSidebarContentContainer");

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  fixed?: boolean;
};
export function AppSidebar({ fixed, ...props }: AppSidebarProps) {
  const { open, setOpen } = useSidebar();
  const [defaultOpen, setDefaultOpen] = useLocalStorage<boolean>(
    "sidebarOpen",
    true,
  );

  const groupSlug = useGroupSlug(false);
  const groupUuid = useGroupUuid();
  const { value: interviewerFeatureFlag } = useSettingData(
    groupUuid,
    "FEATURE_FLAG_INTERVIEWER",
  );
  const userUuid = useUserUuid();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    create: createProjectWithDocument,
    isCreating: isCreatingProjectWithDocument,
  } = useCreateProjectWithDocumentAction(groupUuid);
  const { kits: interviews } = useKitsData(groupUuid, {
    type: "interview",
  });
  const { templates } = useGroupTemplatesData({
    groupUuid,
  });
  const hasExternalContent = useOnContentCheck();
  const matchRoute = useMatchRoute();

  const pathname = location.pathname;

  const userInterviewCount =
    interviews.filter((interview) =>
      interview.user_assignments?.some(
        (assignment) => assignment.user_uuid === userUuid,
      ),
    ).length || 0;

  useEffect(() => setOpen(fixed || defaultOpen), []);
  useEffect(() => {
    if (!fixed) {
      setDefaultOpen(open);
    }
  }, [open]);
  useEffect(() => {
    if (fixed) {
      setOpen(true);
    }
    if (!fixed) {
      setOpen(defaultOpen);
    }
  }, [fixed]);

  const handleCreateDocumentFromTemplate =
    (template?: Template) => async () => {
      const { kit, document } = await createProjectWithDocument(template);
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: {
          groupSlug: groupSlug!,
          kitUuid: kit.uuid,
          documentUuid: document.uuid,
        },
      });
    };

  return (
    <Sidebar collapsible="offcanvas" className="w-[280px]" {...props}>
      <SidebarHeader
        className={cn("border-b", {
          "flex flex-col items-center justify-center": !open,
        })}
      >
        <SidebarGroupSwitcher />
      </SidebarHeader>
      <SidebarContent className="flex flex-col justify-between">
        <div className="flex flex-1 flex-col justify-between">
          <div className="flex flex-col">
            {pathname === `/u/settings` && <UserSettingsMenu />}
            {pathname.startsWith(`/w`) && (
              <SidebarGroup
                className={cn(
                  "flex flex-col gap-1 transition-opacity duration-100",
                  {
                    "not-hover:opacity-50": hasExternalContent,
                  },
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={
                    !!matchRoute({
                      to: "/w/$groupSlug",
                      params: { groupSlug },
                    })
                  }
                  className="item-center flex"
                >
                  <Link to="/w/$groupSlug" params={{ groupSlug: groupSlug! }}>
                    <FontAwesomeIcon icon={faHome} />
                    Home
                  </Link>
                </SidebarMenuButton>
                <SidebarMenuButton
                  asChild
                  className="item-center flex"
                  isActive={
                    !!matchRoute({
                      to: "/w/$groupSlug/workbooks",
                      params: { groupSlug },
                    })
                  }
                >
                  <Link
                    to="/w/$groupSlug/workbooks"
                    params={{ groupSlug: groupSlug! }}
                  >
                    <FontAwesomeIcon
                      icon={getDefaultKitIcon({
                        type: "project",
                      })}
                    />
                    Workbooks
                  </Link>
                </SidebarMenuButton>
                <SidebarMenuButton
                  asChild
                  className="item-center flex"
                  isActive={
                    !!matchRoute({
                      to: "/w/$groupSlug/chats",
                      params: { groupSlug },
                    })
                  }
                >
                  <Link
                    to="/w/$groupSlug/chats"
                    params={{ groupSlug: groupSlug! }}
                  >
                    <FontAwesomeIcon
                      icon={getDefaultKitIcon({
                        type: "conversation",
                      })}
                    />
                    Chats
                  </Link>
                </SidebarMenuButton>
                {interviewerFeatureFlag === "enabled" && (
                  <SidebarMenuButton
                    asChild
                    isActive={
                      !!matchRoute({
                        to: "/w/$groupSlug/interviews",
                        params: { groupSlug },
                      })
                    }
                    className="item-center flex"
                  >
                    <Link
                      to="/w/$groupSlug/interviews"
                      params={{ groupSlug: groupSlug! }}
                    >
                      <FontAwesomeIcon
                        icon={getDefaultKitIcon({
                          type: "interview",
                        })}
                      />
                      Interviews
                      {userInterviewCount > 0 && (
                        <Badge variant="secondary" className="ml-auto">
                          {userInterviewCount}
                        </Badge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                )}
                {!hasExternalContent && (
                  <SidebarGroup className="flex flex-col gap-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button disabled={isCreatingProjectWithDocument}>
                          <FontAwesomeIcon icon={faPlus} /> Create Document
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem
                          onClick={handleCreateDocumentFromTemplate()}
                        >
                          <DocumentIcon />
                          Blank Document
                        </DropdownMenuItem>
                        {templates.length > 0 && <DropdownMenuSeparator />}
                        {templates.map((template) => (
                          <DropdownMenuItem
                            onClick={handleCreateDocumentFromTemplate(template)}
                            key={template.uuid}
                          >
                            <StructuredDocumentIcon /> {template.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarGroup>
                )}
              </SidebarGroup>
            )}

            {hasExternalContent && <Separator />}

            {/* Use AppSidebar.ContentPortal to add contents to the sidebar */}
            <ContentContainer />
          </div>

          {pathname.startsWith(`/w`) && (
            <div className={cn("flex flex-col")}>
              <Separator />
              <div
                className={cn("transition-opacity duration-100", {
                  "not-hover:opacity-50": hasExternalContent,
                })}
              >
                <GroupAdminMenu />
              </div>
            </div>
          )}
        </div>
      </SidebarContent>
      <FeedbackButton className="m-2" label={open ? "Feedback" : null} />
      <SidebarFooter className="border-t">
        <SidebarUser />
      </SidebarFooter>
      {!fixed && (
        <SidebarTrigger className="border-foreground-3 bg-background-2 hover:bg-background-5 absolute top-8 right-0 z-10 flex h-6 w-6 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border">
          <FontAwesomeIcon
            icon={faAngleLeft}
            className={cn("rotate-180 transition-all", open && "rotate-0")}
          />
        </SidebarTrigger>
      )}
    </Sidebar>
  );
}

const AppSidebarWhenOpen: React.FC<
  React.PropsWithChildren<{ visible: boolean }>
> = ({ children, visible }) => {
  const { open } = useSidebar();

  return (
    <div
      className={cn("transition-opacity duration-500", {
        "opacity-0": (visible && !open) || (!visible && open),
        "opacity-100": (visible && open) || (!visible && !open),
      })}
    >
      {children}
    </div>
  );
};

AppSidebar.WhenOpen = AppSidebarWhenOpen;
AppSidebar.ContentPortal = ContentPortal;
